// ═══════════════════════════════════════════════════════════
// ARAMS Dashboard, English Translations
//
// RULE: No hardcoded user-facing text anywhere in the app.
//       Every visible string must come from this file using
//       the t() function from react-i18next.
// ═══════════════════════════════════════════════════════════

const en = {
  // ── Common / Shared ─────────────────────────────────────
  common: {
    appName: 'ARAMS',
    appFullName: 'Agentic Rapid Automated Monitoring & Services',
    tagline: 'Test Platform',
    loading: 'Loading…',
    error: 'Something went wrong',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    create: 'Create',
    update: 'Update',
    close: 'Close',
    copy: 'Copy',
    copied: 'Copied!',
    back: 'Back',
    backTo: 'Back to {{name}}',
    backToProjects: 'Back to Projects',
    backToProject: 'Back to Project',
    noData: 'No data available',
    connected: 'Connected',
    disconnected: 'Disconnected',
    active: 'active',
    archived: 'archived',
    passed: 'passed',
    failed: 'failed',
    running: 'running',
    skipped: 'skipped',
    pending: 'pending',
    cancelled: 'cancelled',
    optional: 'Optional',
    copyright: 'ARAM Services LLC © {{year}}, E2E Test Platform',
  },

  // ── Landing Page (Public Marketing) ─────────────────────
  landing: {
    signIn: 'Sign In',
    getStarted: 'Get Started',
    badge: 'AI-Powered E2E Testing Platform, Now Live',
    heroLine1: 'Test Smarter.',
    heroLine2: 'Ship Faster.',
    heroSub:
      'ARAMS is an AI-powered agentic testing platform that reads your repository, generates Playwright tests, and validates every user role, automatically.',
    ctaPrimary: 'Start Testing Now',
    ctaSecondary: 'See how it works',
    floatAi: 'AI-Driven',
    floatSecurity: 'Enterprise-Grade',
    floatFast: 'Blazing Fast',

    // Stats
    stat1: 'User Roles Tested',
    stat2: 'Test Suites Built-in',
    stat3: 'Automation Coverage',
    stat4: 'Avg. Test Generation',

    // Features
    featuresLabel: 'Features',
    featuresTitle: 'Everything you need to test at scale',
    featuresSub:
      'From AI-generated tests to multi-role authentication matrices, one platform, zero manual scripting.',
    feat1Title: 'AI-Powered Test Generation',
    feat1Desc:
      'Connect your repo and let Gemini AI read the codebase, detect auth flows, suggest tests, and generate ready-to-run Playwright scripts.',
    feat2Title: 'GitHub-Native Workflow',
    feat2Desc:
      'Webhooks trigger tests on every push. PR status checks, branch-aware runs, and commit-linked results, all automatic.',
    feat3Title: '18-Role Test Matrix',
    feat3Desc:
      'Define user roles from Super Admin to Guest. Each role gets its own login flow, permissions scope, and dedicated test path.',
    feat4Title: 'Visual Regression Detection',
    feat4Desc:
      'Pixel-perfect screenshot comparison across runs. Catch UI regressions before they reach production.',
    feat5Title: 'Security-First Architecture',
    feat5Desc:
      'AES-256-GCM encrypted API keys, role-based access control, and automated security checks on every project.',
    feat6Title: 'Real-Time Analytics',
    feat6Desc:
      'Live dashboards with pass/fail trends, duration metrics, flaky test detection, and WebSocket-powered status updates.',

    // How It Works
    howLabel: 'How It Works',
    howTitle: 'From repo to results in 4 steps',
    howSub: 'No manual scripting. No complex configuration. Just connect and go.',
    step1Title: 'Connect Your Repository',
    step1Desc:
      'Link your GitHub repo via personal access token. ARAMS fetches the tree, configs, dependencies, and auth patterns.',
    step2Title: 'AI Analyzes Everything',
    step2Desc:
      'Gemini AI reads your codebase and proposes project details, user roles, test categories, suggested tests, and security checks.',
    step3Title: 'Review & Launch Tests',
    step3Desc:
      'Toggle the tests you want, tweak configurations, and hit "Create". Playwright tests are generated and queued instantly.',
    step4Title: 'Monitor & Iterate',
    step4Desc:
      'Real-time results stream to your dashboard. AI fixes broken tests, detects flaky runs, and suggests improvements.',

    // Tech
    builtWith: 'Built with modern technologies',

    // AI Showcase
    aiLabel: 'AI Agent',
    aiTitle: 'Your repo is the only input',
    aiDesc:
      'The AI agent reads your repository structure, detects frameworks, authentication patterns, user roles, and API routes, then generates a complete E2E test suite tailored to your stack.',
    aiBullet1: 'Reads package.json, tsconfig, docker-compose, README, and key source files',
    aiBullet2: 'Detects login flows, selectors, and role-based access patterns',
    aiBullet3: 'Suggests 15–25 categorized tests with priority and role assignment',
    aiBullet4: 'Flags missing environment info and security vulnerabilities',
    codeComment: 'AI reads your entire repo in seconds',
    codeOutput: 'Result:',

    // CTA
    ctaTitle: 'Ready to automate your testing?',
    ctaDesc:
      'Stop writing tests by hand. Let ARAMS understand your app and generate production-grade E2E tests in seconds.',
    ctaButton: 'Get Started Free',
  },

  // ── Auth / Login ────────────────────────────────────────
  auth: {
    welcomeBack: 'Welcome back',
    createAccount: 'Create account',
    signInSubtitle: 'Sign in to your test platform',
    registerSubtitle: 'Set up your ARAMS account',
    signIn: 'Sign In',
    signUp: 'Create Account',
    switchToRegister: "Don't have an account? Sign up",
    switchToLogin: 'Already have an account? Sign in',
    fullName: 'Full Name',
    email: 'Email',
    password: 'Password',
    namePlaceholder: 'ARAMS Admin',
    emailPlaceholder: 'cto@aram-services.cloud',
    passwordPlaceholder: '••••••••••',
    welcomeToast: 'Welcome, {{name}}!',
    authFailed: 'Authentication failed',
    logout: 'Logout',
    // Password reset
    forgotPassword: 'Forgot password?',
    forgotPasswordTitle: 'Forgot your password?',
    forgotPasswordSubtitle: "Enter your email and we'll send you a reset link",
    sendResetLink: 'Send Reset Link',
    resetEmailSent: 'Reset link sent! Check your email.',
    checkYourEmail: 'Check your email',
    resetEmailSentDesc: 'We sent a password reset link to {{email}}. It expires in 1 hour.',
    backToLogin: 'Back to login',
    resetPasswordTitle: 'Set new password',
    resetPasswordSubtitle: 'Choose a strong password for your account',
    newPassword: 'New Password',
    confirmPassword: 'Confirm Password',
    resetPassword: 'Reset Password',
    passwordsDoNotMatch: 'Passwords do not match',
    passwordTooShort: 'Password must be at least 8 characters',
    passwordResetSuccess: 'Password reset successfully!',
    redirectingToLogin: 'Redirecting to login…',
    invalidResetLink: 'Invalid reset link',
    invalidResetLinkDesc: 'This reset link is invalid or has expired. Request a new one.',
    requestNewLink: 'Request New Link',
    // Account status
    accountDeactivated: 'Account has been deactivated. Contact your administrator.',
  },

  // ── Login Feature Highlights ────────────────────────────
  loginFeatures: {
    aiTitle: 'AI-powered test generation',
    aiDesc: 'Gemini drives intelligent test creation',
    roleTitle: '18-role test matrix',
    roleDesc: 'Comprehensive role-based access testing',
    visualTitle: 'Visual regression detection',
    visualDesc: 'Pixel-perfect screenshot comparison',
  },

  // ── Navigation ──────────────────────────────────────────
  nav: {
    projects: 'Projects',
    settings: 'Settings',
    admin: 'Admin',
  },

  // ── Projects Page ───────────────────────────────────────
  projects: {
    title: 'Projects',
    subtitle: 'Monitor and manage your connected test projects',
    connectProject: 'Connect Project',
    connectFirst: 'Connect First Project',
    noProjectsTitle: 'No projects yet',
    noProjectsDesc:
      'Connect your first project to start running automated tests. ALX Platform is ready to be connected.',
    totalProjects: 'Total Projects',
    totalTests: 'Total Tests',
    testRoles: 'Test Roles',
    passRate: 'Pass Rate',
    suites: 'Suites',
    roles: 'Roles',
    noRunsYet: 'No runs yet',
    passedCount: '{{passed}}/{{total}} passed',
  },

  // ── New / Edit Project Page ──────────────────────────────
  projectForm: {
    createTitle: 'Connect New Project',
    createSubtitle: 'Set up a project to start running automated E2E tests',
    editTitle: 'Edit Project',
    basicInfo: 'Basic Information',
    projectName: 'Project Name',
    projectNamePlaceholder: 'e.g., ALX Platform',
    projectSlug: 'Slug',
    projectSlugPlaceholder: 'e.g., alx-platform',
    projectSlugHint: 'URL-friendly identifier. Auto-generated from name.',
    projectDescription: 'Description',
    projectDescriptionPlaceholder: 'Brief description of the project under test',
    githubSection: 'GitHub Repository',
    githubSectionDesc:
      'Link a GitHub repository to enable webhook-triggered runs and PR status checks. Your GitHub must be connected in Settings first.',
    githubRepo: 'Repository (owner/repo)',
    githubRepoPlaceholder: 'e.g., ARAMS-CTO/TSP_Azure',
    githubRepoPick: 'Or select from your repos:',
    githubBranch: 'Default Branch',
    githubBranchPlaceholder: 'main',
    targetSection: 'Target Application',
    targetUrl: 'Target URL',
    targetUrlPlaceholder: 'e.g., https://app.example.com',
    authSection: 'Authentication Selectors',
    authSectionDesc:
      'CSS selectors for the login form, used by Playwright to authenticate test roles',
    loginUrl: 'Login Page Path',
    loginUrlPlaceholder: '/auth',
    usernameSelector: 'Username/Email Selector',
    usernameSelectorPlaceholder: "input[name='email']",
    passwordSelector: 'Password Selector',
    passwordSelectorPlaceholder: "input[name='password']",
    submitSelector: 'Submit Button Selector',
    submitSelectorPlaceholder: "button[type='submit']",
    aiSection: 'AI Configuration',
    aiSectionDesc:
      'Choose an AI provider and model for test generation. The platform uses Gemini by default, add your own API key for any provider.',
    aiProvider: 'AI Provider',
    aiModel: 'AI Model',
    aiUserKeyRequired: 'your API key',
    aiPlatformAvailable: 'Platform configured, ready to use',
    aiPlatformNotAvailable: 'Not configured on platform, add your own API key below',
    aiApiKey: 'API Key',
    aiApiKeyOptional: 'optional if platform provider is configured',
    aiApiKeyDesc:
      'Your key overrides the platform default. It will be encrypted (AES-256-GCM) before storage.',
    aiApiKeyPlaceholder: 'sk-… or AIza…',
    aiApiKeyEncrypted: 'Will be encrypted before saving',
    createProject: 'Create Project',
    creating: 'Creating…',
    projectCreated: 'Project created successfully!',
    validationNameRequired: 'Project name is required',
    validationSlugRequired: 'Slug is required',
    loading: 'Loading…',
    noReposFound: 'No repos found',
    githubNotConnectedPre: 'Connect your GitHub account in',
    githubNotConnectedSettings: 'Settings',
    githubNotConnectedPost: 'to pick repos and enable webhooks.',
  },

  // ── Project Wizard (AI-powered creation) ─────────────────
  projectWizard: {
    // Steps
    stepSetup: 'Setup',
    stepAgent: 'AI Agent',
    stepRepo: 'Select Repository',
    stepAnalyze: 'AI Analysis',
    stepReview: 'Review & Create',

    // Main
    title: 'Connect New Project',
    subtitle: 'AI-powered project setup, select a repo and let the agent do the rest',
    simpleSubtitle:
      'Give your project a name, pick a GitHub repo, our AI agent handles everything else.',
    agentWorking: 'Our AI agent is setting up your project…',

    // GitHub
    githubRequired: 'GitHub Connection Required',
    githubRequiredDesc:
      'Connect your GitHub account in Settings first, then come back to link a repository.',
    goToSettings: 'Go to Settings',

    // Repo selection
    selectRepo: 'Select a Repository',
    selectRepoDesc: 'Enter a repository (owner/repo) or pick from your connected repos below.',
    selectRepoSimple: 'Pick the repository you want to test. Our AI agent will handle the rest.',
    repoPlaceholder: 'e.g., ARAMS-CTO/TSP_Azure',
    browseRepos: 'Browse your repos',
    searchRepos: 'Search repos…',
    noRepos: 'No repos found',
    analyzeWithAi: 'Analyze with AI',
    invalidRepo: 'Enter a valid owner/repo (e.g., ARAMS-CTO/TSP_Azure)',

    // Project name
    projectName: 'Project Name',
    projectNameHint:
      'Give your project a name. This will be auto-filled from the repo if left empty.',
    projectNamePlaceholder: 'e.g., My Awesome App',
    projectNameTip:
      'A human-friendly name for your project. Auto-fills from the repo name if empty.',

    // Project details card
    projectDetails: 'Project Details',
    projectDetailsDesc:
      'Provide a name and the URL of the application you want to test. The AI agent will fill in everything else.',
    projectDetailsTip:
      'The name identifies your project in the dashboard. The target URL is where the AI will run tests against.',

    // Target URL
    targetUrl: 'Target URL',
    targetUrlPlaceholder: 'https://your-app.com',
    targetUrlHint: 'The live or staging URL where the application is deployed.',
    targetUrlTip:
      'This is the URL the AI agent and Playwright tests will navigate to. Use your staging or production URL.',

    // Branch
    selectBranch: 'Branch',
    branchTip:
      'The branch to analyze. The AI reads the codebase from this branch to detect tech stack, auth, and test targets.',
    branchHint: 'Branches are detected automatically from your GitHub repository.',
    loadingBranches: 'Fetching branches from GitHub…',
    noBranches: 'No branches found',

    // Repo
    selectRepoTip:
      'Select the repository that contains the source code you want to test. The AI agent reads the code to generate tests.',

    // What the agent does
    whatAgentDoes: 'What the AI agent will do:',
    agentBullet1: 'Scan repository structure and tech stack',
    agentBullet2: 'Detect authentication flows and login patterns',
    agentBullet3: 'Identify user roles and permissions',
    agentBullet4: 'Generate test suites and test cases automatically',
    agentBullet5: 'Create smoke tests ready to run immediately',

    // Launch
    launchAgent: 'Launch AI Agent',

    // AI Agent working
    agentTitle: 'AI Agent is working…',
    agentSubtitle: 'Analyzing {{repo}} and setting up your project automatically.',

    // Analysis states
    analyzing: 'AI is analyzing your repository…',
    analyzingDesc: 'Reading {{repo}}, structure, dependencies, auth, roles, and tests…',
    analyzingSteps: 'Fetching tree → Reading configs → Building analysis…',
    analysisFailed: 'Analysis Failed',
    retry: 'Retry',

    // Legacy keys (kept for compatibility)
    changeRepo: 'Change repository',
    slug: 'Slug',
    slugHint: 'URL-friendly identifier, auto-generated from name.',
    description: 'Description',
    techStack: 'Tech Stack',
    analyzedWith: 'Analyzed with {{model}}',
    authDetection: 'Authentication Detection',
    authDetected: '{{method}} authentication detected',
    noAuthDetected: 'No authentication layer detected, tests will run without login.',
    loginPath: 'Login path',
    usernameField: 'Username field',
    passwordField: 'Password field',
    submitBtn: 'Submit button',
    detectedRoles: 'Detected Roles ({{count}})',
    testCategories: 'Test Categories',
    selected: 'selected',
    tests: 'tests',
    suggestedTests: 'Suggested Tests',
    selectAll: 'Select all',
    deselectAll: 'Deselect all',
    missingInfo: 'Missing Information',
    securityChecks: 'Security Checks',
    creating: 'Creating…',
    createProject: 'Create Project ({{count}} tests)',
    projectCreated: 'Project created successfully!',
  },

  // ── Project Detail Page ─────────────────────────────────
  projectDetail: {
    analytics: 'Analytics',
    aiAgent: 'AI Agent',
    runTests: 'Run Tests',
    recentRuns: 'Recent Test Runs',
    noRunsHint: 'No runs yet. Click "Run Tests" to start.',
    runStarted: 'Test run started!',
    configuration: 'Configuration',
    testRolesCount: 'Test Roles ({{count}})',
    testSuitesCount: 'Test Suites ({{count}})',
    totalRuns: 'Total Runs',
    runLabel: 'Run #{{id}}',
    readyToTest: 'Ready to test!',
    readyToTestDesc:
      'Your project is set up. Select tests and click Run to start your first automated test run.',
    aiSuggestedTests: 'AI-Suggested Tests',
    runTestsNow: 'Run Tests Now',
    deleteProject: 'Delete Project',
    deleteProjectConfirm: 'Are you sure you want to permanently delete this project?',
    deleteProjectWarning:
      'This will delete all test roles, suites, runs, and results. This action cannot be undone.',
    projectDeleted: 'Project deleted successfully',
  },

  // ── Run Detail Page ─────────────────────────────────────
  runDetail: {
    title: 'Run #{{id}}',
    triggerInfo: '{{trigger}} trigger • {{branch}} {{commit}}',
    totalTests: 'Total Tests',
    passedTests: 'Passed',
    failedTests: 'Failed',
    skippedTests: 'Skipped',
    duration: 'Duration',
    passRateLabel: 'Pass Rate',
    testResults: 'Test Results',
    waitingResults: 'Waiting for test results…',
    noResults: 'No test results available',
    error: 'Error',
    steps: 'Steps:',
    retries: '{{count}} retries',
  },

  // ── Analytics Page ──────────────────────────────────────
  analytics: {
    title: 'Analytics',
    subtitle: 'Test intelligence for {{name}}, Last 30 days',
    totalRuns: 'Total Runs',
    passedRuns: 'Passed Runs',
    failedRuns: 'Failed Runs',
    avgDuration: 'Avg Duration',
    passFailTrend: 'Pass / Fail Trend',
    coverageByType: 'Test Coverage by Type',
    flakyDetection: 'Flaky Test Detection',
    noTrendData: 'No trend data yet',
    noCoverageData: 'No coverage data yet',
    noFlakyTests: 'No flaky tests detected. Keep it up! 🎉',
    passedLabel: '{{count}} passed',
    failedLabel: '{{count}} failed',
  },

  // ── AI Agent Page ───────────────────────────────────────
  ai: {
    title: 'AI Agent',
    subtitle: 'Powered by {{model}}, Generate tests, fix failures, and create seed data',
    tabGenerate: 'Generate Test',
    tabSeed: 'Generate Seed',
    tabFix: 'Fix Test',
    tabHistory: 'History',
    generatePlaywright: 'Generate Playwright Test',
    describeTest: 'Describe what to test',
    testDescPlaceholder:
      'e.g., Test that a PM can create a new enquiry, add products, set quantities, assign a factory, and submit the enquiry. Verify the enquiry appears in the dashboard.',
    roleName: 'Role Name (optional)',
    roleNamePlaceholder: 'e.g., AsiaLink_PM',
    testType: 'Test type',
    testTypeE2E: 'E2E Test',
    testTypeAuth: 'Auth Test',
    testTypeVisual: 'Visual Test',
    testTypeAPI: 'API Test',
    generate: 'Generate',
    generateSeedTitle: 'Generate Seed Data',
    seedDescLabel: 'Describe the seed data needed',
    seedDescPlaceholder:
      'e.g., Create 5 test clients, 3 factories, 10 enquiries with various statuses, and linked purchase orders.',
    generateSeed: 'Generate Seed',
    fixTitle: 'Fix Failing Test',
    testName: 'Test Name',
    testNamePlaceholder: 'e.g., PM Full Workflow > creates enquiry',
    errorMessage: 'Error Message',
    errorPlaceholder: 'Paste the full error message and stack trace…',
    suggestFix: 'Suggest Fix',
    historyTitle: 'Generation History',
    noHistory: 'No AI generations yet',
    generatedOutput: 'Generated Output',
    outputPlaceholder: 'Generated code will appear here',
    generatedWith: 'Generated with {{model}}',
    seedGenerated: 'Seed data generated!',
    fixGenerated: 'Fix suggestion generated!',
    model: 'Model: {{model}}',
  },

  // ── Settings Page ───────────────────────────────────────
  settings: {
    title: 'Settings',
    subtitle: 'Platform configuration and user preferences',
    profile: 'Profile',
    name: 'Name',
    email: 'Email & Notifications',
    role: 'Role:',
    updateProfile: 'Update Profile',
    apiKeys: 'API Keys & AI Models',
    aiApiKeyLabel: 'Gemini AI API Key',
    aiApiKeyPlaceholder: 'Enter your Google AI API key',
    platformProvidesAi: 'Gemini AI is platform-provided',
    platformProvidesAiDesc:
      'The platform includes Gemini AI models. You can use them without any additional configuration.',
    personalApiKeyLabel: 'Personal API Key',
    personalApiKeyDesc:
      'Optionally provide your own API key if you want to use a different provider (OpenAI, Anthropic) or your own Gemini quota.',
    personalApiKeyPlaceholder: 'sk-… or AIza…',
    githubTokenLabel: 'GitHub Personal Access Token',
    githubTokenPlaceholder: 'ghp_xxxxxxxxxxxxxxxxxxxx',
    githubTokenHint:
      "Classic token: needs repo + admin:repo_hook scopes. Fine-grained token: set Repository Access to 'All repositories' — not specific repos, or only those repos will appear.",
    saveKeys: 'Save Keys',
    keysSavedToast: 'API keys saved (in-memory only, configure .env for persistence)',
    database: 'Database',
    dbHost: 'Host',
    dbPort: 'Port',
    dbName: 'Database',
    dbStatus: 'Status',
    platformInfo: 'Platform Info',
    version: 'Version',
    domain: 'Domain',
    stack: 'Stack',
    stackValue: 'React 19 + Express 5 + Playwright',
    aiEngine: 'AI Engine',
    aiEngineValue: 'Gemini',
    github: 'GitHub Integration',
    githubConnection: 'GitHub OAuth',
    githubDesc: 'Connect your GitHub account to link repos and enable CI webhooks',
    connectGithub: 'Connect with GitHub',
    disconnectGithub: 'Disconnect',
    githubConnected: 'Connected as {{login}}',
    githubDisconnected: 'GitHub disconnected',
    githubTokenSaved: 'GitHub token saved & verified!',
    saveToken: 'Save Token',
    notConnected: 'Not connected',
    smtpStatus: 'SMTP Server',
    smtpFrom: 'From Address',
    smtpTransport: 'Transport',
    sendTestEmail: 'Send Test Email',
    testEmailPlaceholder: 'recipient@example.com',
    testEmailSent: 'Test email sent successfully!',
    // Run Builder
    runBuilder: 'Run Test Suite',
    runBuilderDesc: 'Configure and launch a new Playwright test run',
    selectBranch: 'Branch',
    selectSuites: 'Test Suites',
    selectBrowsers: 'Browsers',
    retries: 'Retries',
    workers: 'Workers',
    headed: 'Headed mode',
    launchRun: 'Launch Run',
    allSuites: 'All Suites',
    allBrowsers: 'All Browsers',
  },

  // ── Admin Page ──────────────────────────────────────────
  admin: {
    title: 'Admin Panel',
    subtitle: 'Manage users, licenses, and platform access',
    totalUsers: 'Total Users',
    activeUsers: 'Active Users',
    totalProjects: 'Total Projects',
    proLicenses: 'Pro+ Licenses',
    allUsers: 'All Users',
    user: 'User',
    role: 'Role',
    license: 'License',
    status: 'Status',
    projects: 'Projects',
    lastActive: 'Last Active',
    actions: 'Actions',
    active: 'Active',
    disabled: 'Disabled',
    never: 'Never',
    expiresOn: 'Expires {{date}}',
    editUser: 'Edit User',
    licenseTier: 'License Tier',
    licenseExpiry: 'License Expires',
    licenseExpiryHint: 'Leave empty for no expiry (unlimited)',
    accountActive: 'Account Active',
    accountDisabled: 'Account Disabled',
    userUpdated: 'User updated successfully',
    userDeleted: 'User deleted successfully',
    confirmDelete: 'Delete User?',
    confirmDeleteDesc:
      'This action is permanent. The user will lose access and be removed from all projects.',
  },
} as const;

export default en;
